import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  User, 
  Repository, 
  SearchParams, 
  SearchResult, 
  AIAnalysisResult, 
  Favorite, 
  LoginData, 
  RegisterData, 
  ChatMessage,
  SearchSuggestions,
  ApiResponse 
} from '@/types';

class ApiService {
  private api: AxiosInstance;
  private searchAbortController: AbortController | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: '/api',
      timeout: 30000,
      withCredentials: true,
    });

    // 请求拦截器
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('session_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          console.log('⚠️ 收到401响应，但不立即清除认证信息');
          // 不立即清除认证信息，让用户继续使用
          // 只有在关键操作（如登录、注册）失败时才清除
          // 这样可以避免因为网络问题导致的误清除
        }
        return Promise.reject(error);
      }
    );
  }

  // 认证相关API
  async sendSMS(phone: string): Promise<ApiResponse> {
    const response = await this.api.post('/auth/send-sms', { phone });
    return response.data;
  }

  async register(data: RegisterData): Promise<ApiResponse<{ user: User; session_token: string }>> {
    const response = await this.api.post('/auth/register', data);
    if (response.data.success && response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }

  async login(data: LoginData): Promise<ApiResponse<{ user: User; session_token: string }>> {
    console.log('🔍 API登录请求:', { account: data.account, password: '***' });
    try {
      const response = await this.api.post('/auth/login', data);
      console.log('📡 API登录响应:', response.data);

      if (response.data.success && response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        console.log('✅ 登录信息已保存到localStorage');
      }
      return response.data;
    } catch (error: any) {
      console.error('❌ API登录请求失败:', error);
      throw error;
    }
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.api.post('/auth/logout');
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    return response.data;
  }

  async getProfile(): Promise<ApiResponse<{ user: User }>> {
    const response = await this.api.get('/auth/profile');
    return response.data;
  }

  async updateProfile(data: Partial<User> & {
    new_password?: string;
    qianwen_api_key?: string;
    github_token?: string;
  }): Promise<any> {
    const response = await this.api.put('/auth/profile', data);
    if (response.data.success && response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }

  // 搜索相关API
  async searchProjects(params: SearchParams, isAuthenticated: boolean = false): Promise<SearchResult> {
    // 取消之前的搜索请求
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }

    // 创建新的AbortController
    this.searchAbortController = new AbortController();

    // 根据登录状态选择不同的端点
    const endpoint = isAuthenticated ? '/search' : '/search/public';

    const response = await this.api.post(endpoint, params, {
      signal: this.searchAbortController.signal
    });
    return response.data;
  }

  // 取消搜索请求
  cancelSearch(): void {
    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }
  }

  async getSearchSuggestions(query?: string, limit?: number): Promise<SearchSuggestions> {
    const response = await this.api.get('/search/suggestions', {
      params: { q: query, limit }
    });
    return response.data;
  }

  // 获取单个仓库信息
  async getRepositoryInfo(fullName: string): Promise<Repository | null> {
    try {
      const response = await this.api.get(`/search/repository/${encodeURIComponent(fullName)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch repository info:', error);
      return null;
    }
  }

  // AI功能相关API
  async analyzeProject(project: Repository, language: string = 'zh'): Promise<AIAnalysisResult> {
    const response = await this.api.post('/ai/analyze', {
      ...project,
      language
    });
    return response.data;
  }

  async deepAnalyzeProject(project: Repository, analysisType: string = 'comprehensive', language: string = 'zh'): Promise<AIAnalysisResult> {
    const response = await this.api.post('/ai/deep-analyze', {
      ...project,
      analysis_type: analysisType,
      language
    });
    return response.data;
  }

  async applicationAnalyzeProject(project: Repository, focus: string = 'general', language: string = 'zh'): Promise<AIAnalysisResult> {
    const response = await this.api.post('/ai/application-analyze', {
      ...project,
      focus,
      language
    });
    return response.data;
  }

  // 检查项目是否有缓存
  async checkProjectCache(project: Repository): Promise<any> {
    const response = await this.api.post('/ai/check-project-cache', {
      name: project.name,
      full_name: project.full_name,
      description: project.description,
      owner: project.owner,
      stars: project.stars,
      forks: project.forks
    });
    return response.data;
  }

  async chatWithAI(message: string): Promise<ApiResponse<ChatMessage>> {
    const response = await this.api.post('/ai/chat', { message });
    return response.data;
  }

  // GitHub Trending 相关API
  async getTrendingProjects(date: string): Promise<any> {
    try {
      const response = await this.api.get(`/trending/projects/${date}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch trending projects:', error);
      throw error;
    }
  }

  async getTodayTrending(): Promise<any> {
    try {
      const response = await this.api.get('/trending/today');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch today trending:', error);
      throw error;
    }
  }

  async getTrendingDates(): Promise<any> {
    try {
      const response = await this.api.get('/trending/dates');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch trending dates:', error);
      throw error;
    }
  }

  async getAvailableDates(limit: number = 7): Promise<any> {
    try {
      const response = await this.api.get(`/trending/available-dates?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch available dates:', error);
      throw error;
    }
  }

  async getDataStatus(): Promise<any> {
    try {
      const response = await this.api.get('/trending/data-status');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch data status:', error);
      throw error;
    }
  }

  // 管理员专用API
  async fetchTodayTrending(): Promise<any> {
    try {
      // 爬虫获取数据需要时间，设置1分钟超时
      const response = await this.api.post('/trending/fetch-today', {}, {
        timeout: 60000 // 1分钟超时
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch today trending data:', error);
      throw error;
    }
  }

  async generateAIInterpretations(date?: string): Promise<any> {
    try {
      // AI解读需要更长时间，设置2分钟超时
      const response = await this.api.post('/trending/generate-ai-interpretations', {
        date
      }, {
        timeout: 120000 // 2分钟超时
      });
      return response.data;
    } catch (error) {
      console.error('Failed to generate AI interpretations:', error);
      throw error;
    }
  }

  async getTrendingAdminStatus(): Promise<any> {
    try {
      const response = await this.api.get('/trending/admin/status');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch trending admin status:', error);
      throw error;
    }
  }

  async getUpdateStatus(): Promise<any> {
    try {
      const response = await this.api.get('/trending/update-status');
      return response.data;
    } catch (error) {
      console.error('Failed to get update status:', error);
      throw error;
    }
  }

  async getErrorMonitor(hours: number = 24): Promise<any> {
    try {
      const response = await this.api.get(`/trending/error-monitor?hours=${hours}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get error monitor:', error);
      throw error;
    }
  }

  async getDataStats(days: number = 30): Promise<any> {
    try {
      const response = await this.api.get(`/trending/data-stats?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get data stats:', error);
      throw error;
    }
  }

  async cleanupOldData(retentionDays: number = 30): Promise<any> {
    try {
      const response = await this.api.post('/trending/cleanup-data', { retention_days: retentionDays });
      return response.data;
    } catch (error) {
      console.error('Failed to cleanup old data:', error);
      throw error;
    }
  }

  async getCacheStats(): Promise<any> {
    try {
      const response = await this.api.get('/trending/cache-stats');
      return response.data;
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      throw error;
    }
  }

  async clearCache(type: string = 'all'): Promise<any> {
    try {
      const response = await this.api.post('/trending/clear-cache', { type });
      return response.data;
    } catch (error) {
      console.error('Failed to clear cache:', error);
      throw error;
    }
  }

  // 收藏相关API
  async getFavorites(): Promise<ApiResponse<{ favorites: Favorite[]; total_count: number }>> {
    const response = await this.api.get('/favorites');
    return response.data;
  }

  async addFavorite(project: Repository): Promise<ApiResponse<{ favorite: Favorite }>> {
    const response = await this.api.post('/favorites', {
      project_name: project.name,
      project_full_name: project.full_name,
      project_url: project.url,
      project_description: project.description,
      project_stars: project.stars,
      project_language: project.language
    });
    return response.data;
  }

  async removeFavorite(favoriteId: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/favorites/${favoriteId}`);
    return response.data;
  }

  async checkFavorite(projectFullName: string): Promise<ApiResponse<{ is_favorited: boolean; favorite_id?: number }>> {
    const response = await this.api.post('/favorites/check', {
      project_full_name: projectFullName
    });
    return response.data;
  }

  async toggleFavorite(project: Repository): Promise<ApiResponse<{ action: 'added' | 'removed'; is_favorited: boolean; favorite?: Favorite }>> {
    const response = await this.api.post('/favorites/toggle', {
      project_name: project.name,
      project_full_name: project.full_name,
      project_url: project.url,
      project_description: project.description,
      project_stars: project.stars,
      project_language: project.language
    });
    return response.data;
  }

  // 健康检查
  async healthCheck(): Promise<ApiResponse> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Token池管理相关API
  async getTokenPool(): Promise<ApiResponse> {
    const response = await this.api.get('/token-pool/');
    return response.data;
  }

  async addToken(tokenData: any): Promise<ApiResponse> {
    const response = await this.api.post('/token-pool/', tokenData);
    return response.data;
  }

  async updateToken(tokenId: number, tokenData: any): Promise<ApiResponse> {
    const response = await this.api.put(`/token-pool/${tokenId}`, tokenData);
    return response.data;
  }

  async deleteToken(tokenId: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/token-pool/${tokenId}`);
    return response.data;
  }

  async getTokenPoolStats(): Promise<ApiResponse> {
    const response = await this.api.get('/token-pool/stats');
    return response.data;
  }

  // 管理员用户管理相关API
  async getUsers(): Promise<ApiResponse<{ users: User[]; total_count: number }>> {
    const response = await this.api.get('/admin/users');
    return response.data;
  }

  async toggleUserActive(userId: number): Promise<ApiResponse> {
    const response = await this.api.put(`/admin/users/${userId}/toggle-active`);
    return response.data;
  }

  async toggleUserAdmin(userId: number): Promise<ApiResponse> {
    const response = await this.api.put(`/admin/users/${userId}/toggle-admin`);
    return response.data;
  }

  async resetUserPassword(userId: number, newPassword: string): Promise<ApiResponse> {
    const response = await this.api.put(`/admin/users/${userId}/reset-password`, {
      new_password: newPassword
    });
    return response.data;
  }

  async deleteUser(userId: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/users/${userId}`);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
